import type { StateCreator } from 'zustand';
// eslint-disable-next-line import/no-cycle
import { useStore } from './index';
import type {
  ContentGeneration,
  ContentStylePreset,
  ContentRatingsSummary,
  ContentPosted,
  ContentRatingValue,
  ImageTemplate,
  CarouselData,
  ContentType,
  ContentFormatDef,
  TemplateRanking,
  ContentPlan,
  ContentPlanSlot,
  FormatPerformance,
  RuleSuggestion,
  CriticStats,
  AIAnalysis,
  ContentExperiment,
  ExperimentSuggestion,
  ExperimentMatrix,
  WeekSlotSuggestion,
  WeekSlotGeneration,
  WeekPlanPhase,
  SlideOverrides,
} from '../types';
import { contentStudioApi } from '../services/contentStudioApi';
import type { PoolStats, EngineConfig, CockpitScores, PromptArtifacts } from '../services/contentStudioApi';
import { ApiError } from '../services/api';
import { isDayPast } from '../components/content-studio/WeekPlanGrid';

export const SLIDER_DEFAULTS = {
  aggressiveness: 50,
  aspectFocus: 'pain' as const,
  toneFormality: 50,
  // Default to full "Kurs-Konkret": auto-grounds body slides in the strategy's
  // knowledge sources and unlocks the concrete (non-teaser) generation path.
  mehrwert: 100,
  // Kontrast-Metrik-Modus: opt-in, nur für metrik-freundliche Themen.
  metricAccumulation: false,
};

export interface CsFormState {
  contentType: ContentType;
  contentFormat: 'image' | 'video';
  contentTypePerFormat: { image: ContentType; video: ContentType };
  model: 'haiku' | 'sonnet';
  contentLang: 'en' | 'de' | 'es';
  customRules: string;
  presetId: number | '';
  experiments: string[];
  topicAngle: string;
  tensionCategory: string | null;
  hookFormula: string | null;
  viralHookCategory: string | null;
  viralHookId: string | null;
  autoSequence: boolean;
  aggressiveness: number;
  aspectFocus: 'pain' | 'desire' | 'transformation';
  toneFormality: number;
  mehrwert: number;
  /** Kontrast-Metrik-Modus: akkumulierender Ist-vs-Ziel-Kontrast. Default false. */
  metricAccumulation: boolean;
  documentIds: number[];
  documentIdsTouchedByUser?: boolean;
  excludedBookIds: number[];
  includedBookIds: number[];
  /** Phase 2.10 · global block-layout preference for body slides during
   *  carousel generation. "auto" or unset = AI free choice. */
  bodyLayoutPreference?: string;
  /** Phase 1 (warm-dijkstra) · per-role layout preferences. "auto" or unset
   *  falls back to DEFAULT_LAYOUT_BY_ROLE on the backend. */
  hookLayoutPreference?: string;
  ctaLayoutPreference?: string;
}

export interface ContentStudioSlice {
  contentGenerations: ContentGeneration[];
  contentTotal: number;
  contentPresets: ContentStylePreset[];
  contentPromptPreview: { system_prompt: string; user_message: string; artifacts?: PromptArtifacts } | null;
  // Wiederverwendbarer Prompt-Inspector, den JEDE AI-Aktion triggern kann (Phase 3A).
  promptInspector: {
    title?: string;
    systemPrompt: string;
    userMessage: string;
    artifacts?: PromptArtifacts;
    onApply?: (editedUser: string) => void;  // gesetzt => User-Message editierbar + "Für Generierung verwenden"
  } | null;
  contentRatingsSummary: ContentRatingsSummary | null;
  contentGenerating: boolean;
  contentRefining: boolean;
  competitorPosts: Array<{ id: number; caption: string; viral_score: number; owner_username: string }>;
  postedContent: Record<number, ContentPosted>;
  imageTemplates: ImageTemplate[];
  carouselData: Record<number, CarouselData>;

  generateContent: (
    strategyId: number,
    data: Parameters<typeof contentStudioApi.generate>[1]
  ) => Promise<ContentGeneration | null>;
  refineContent: (
    strategyId: number,
    genId: number,
    feedback: string,
    model?: string
  ) => Promise<ContentGeneration | null>;
  regenerateContent: (
    strategyId: number,
    genId: number,
    model?: string,
    opts?: { preserveTemplate?: boolean }
  ) => Promise<ContentGeneration | null>;
  previewContentPrompt: (
    strategyId: number,
    data: Parameters<typeof contentStudioApi.previewPrompt>[1]
  ) => Promise<void>;
  clearContentPromptPreview: () => void;
  openPromptInspector: (cfg: { title?: string; systemPrompt: string; userMessage: string; artifacts?: PromptArtifacts; onApply?: (editedUser: string) => void }) => void;
  closePromptInspector: () => void;
  fetchGenerations: (
    strategyId: number,
    params?: Parameters<typeof contentStudioApi.listGenerations>[1]
  ) => Promise<void>;
  toggleStarGeneration: (strategyId: number, genId: number, starred: boolean) => Promise<void>;
  deleteGeneration: (strategyId: number, genId: number) => Promise<void>;
  rateContent: (
    strategyId: number,
    genId: number,
    rating: ContentRatingValue,
    notes?: string
  ) => Promise<void>;
  fetchContentPresets: (strategyId: number) => Promise<void>;
  createContentPreset: (strategyId: number, data: Partial<ContentStylePreset>) => Promise<void>;
  deleteContentPreset: (strategyId: number, presetId: number) => Promise<void>;
  fetchRatingsSummary: (strategyId: number) => Promise<void>;
  fetchCompetitorPosts: (strategyId: number, limit?: number) => Promise<void>;
  markAsPosted: (
    strategyId: number,
    genId: number,
    post_url: string,
    scrape_intervals: string[]
  ) => Promise<void>;
  fetchEngagement: (strategyId: number, genId: number) => Promise<void>;
  fetchImageTemplates: (force?: boolean) => Promise<void>;

  // Content Formats
  contentFormats: ContentFormatDef[];
  templateRankings: TemplateRanking[];
  selectedFormat: string | null;
  rankingLoading: boolean;
  setContentFormats: (formats: ContentFormatDef[]) => void;
  setTemplateRankings: (rankings: TemplateRanking[]) => void;
  setSelectedFormat: (format: string | null) => void;
  setRankingLoading: (loading: boolean) => void;
  fetchContentFormats: () => Promise<void>;
  rankTemplatesForText: (strategyId: number, text: string, contentFormat?: string) => Promise<void>;

  // Content Plan
  activePlan: ContentPlan | null;
  planSlots: ContentPlanSlot[];
  todayTimeline: ContentPlanSlot[];
  planLoading: boolean;
  fetchPlan: (strategyId: number, channelId?: number) => Promise<void>;
  createPlan: (strategyId: number, data: Partial<ContentPlan> & { cycle_type?: string; name?: string }, channelId?: number) => Promise<void>;
  updatePlan: (strategyId: number, planId: number, data: Partial<ContentPlan>) => Promise<void>;
  deletePlan: (strategyId: number, planId: number) => Promise<void>;
  fetchTodayTimeline: (strategyId: number) => Promise<void>;
  addPlanSlot: (strategyId: number, planId: number, data: Partial<ContentPlanSlot>) => Promise<void>;
  updatePlanSlot: (strategyId: number, planId: number, slotId: number, data: Partial<ContentPlanSlot>) => Promise<void>;
  deletePlanSlot: (strategyId: number, planId: number, slotId: number) => Promise<void>;
  revertPlanSlot: (strategyId: number, planId: number, slotId: number) => Promise<void>;

  // Format Performance
  formatPerformance: FormatPerformance[];
  performanceSuggestions: RuleSuggestion[];
  aiAnalysis: AIAnalysis | null;
  analysisLoading: boolean;
  fetchFormatPerformance: (strategyId: number) => Promise<void>;
  fetchAIAnalysis: (strategyId: number) => Promise<void>;
  runAutoAdjust: (strategyId: number) => Promise<void>;

  // Critic Stats (Phase 2: Adversarial Critic Observability)
  criticStats: CriticStats | null;
  criticStatsLoading: boolean;
  criticStatsError: string | null;
  fetchCriticStats: (strategyId: number, days?: number) => Promise<void>;

  // Cockpit locks (persisted per strategy on backend)
  cockpitLocks: Record<number, Record<string, { value: string; locked_at: string; hard: boolean }>>;
  loadCockpitLocks: (strategyId: number) => Promise<void>;
  setCockpitLock: (strategyId: number, field: string, value: string, hard?: boolean) => Promise<void>;
  clearCockpitLock: (strategyId: number, field: string) => Promise<void>;

  // Layer-12 active sources (persistent per strategy on backend). Order in the
  // array determines autopilot iteration order. Empty = no filter (legacy
  // behavior: all sources eligible for topic suggestions).
  layer12Sources: Record<number, Array<{ type: 'book' | 'lesson'; id: string | number }>>;
  layer12Loading: Record<number, boolean>;
  loadLayer12Sources: (strategyId: number) => Promise<void>;
  updateLayer12Sources: (
    strategyId: number,
    sources: Array<{ type: 'book' | 'lesson'; id: string | number }>,
  ) => Promise<void>;
  toggleLayer12Source: (
    strategyId: number,
    source: { type: 'book' | 'lesson'; id: string | number },
  ) => Promise<void>;

  // Autopilot · next topic preview (server-resolved from Layer 12)
  poolNextTopic: Record<number, {
    topic: any | null;
    exhausted: boolean;
    reason: 'ok' | 'no_active_sources' | 'all_exhausted';
    active_sources_count: number;
  } | null>;
  poolNextLoading: Record<number, boolean>;
  loadPoolNextTopic: (strategyId: number) => Promise<void>;

  // Viral hooks
  viralHookCategories: Array<{ slug: string; label: string; count: number }>;
  viralHooksByCategory: Record<string, any[]>;
  loadViralHookCategories: (strategyId: number) => Promise<void>;
  loadViralHooks: (strategyId: number, category: string) => Promise<void>;

  // Per-strategy form persistence
  csFormState: Record<number, CsFormState>;
  csSubTab: 'create' | 'history' | 'analytics' | 'plan' | 'batch' | 'queue';
  csSelectedStrategyId: number | null;
  csCurrentGeneration: Record<number, ContentGeneration | null>;
  setCsFormState: (strategyId: number, form: Partial<CsFormState>) => void;
  setCsSubTab: (tab: 'create' | 'history' | 'analytics' | 'plan' | 'batch' | 'queue') => void;
  setCsSelectedStrategyId: (id: number | null) => void;
  setCsCurrentGeneration: (strategyId: number, gen: ContentGeneration | null) => void;

  // Multi-platform generation
  multiPlatformGenerations: ContentGeneration[];
  multiPlatformLoading: boolean;
  generateMultiPlatform: (strategyId: number, data: import('../types').MultiPlatformGenerationRequest) => Promise<ContentGeneration[]>;

  // Batch generation
  batchTopics: import('../types').BatchTopic[];
  batchGenerations: ContentGeneration[];
  batchErrors: string[];
  batchGenerating: boolean;
  suggestingTopics: boolean;
  setBatchTopics: (topics: import('../types').BatchTopic[]) => void;
  suggestBatchTopics: (strategyId: number, opts?: { n?: number; content_lang?: string }) => Promise<void>;
  batchGenerate: (strategyId: number, data: { topics: import('../types').BatchTopic[]; model?: string; competitor_post_ids?: number[] }) => Promise<ContentGeneration[]>;

  // LivePreview -> Generate bridge
  previewSlides: Array<{ text: string; role: string; content_type?: string; data?: Record<string, unknown> }> | null;
  setPreviewSlides: (slides: Array<{ text: string; role: string; content_type?: string; data?: Record<string, unknown> }> | null) => void;

  // Carousel
  fetchCarouselSlides: (strategyId: number, genId: number) => Promise<void>;
  updateSlideText: (strategyId: number, genId: number, slideNumber: number, text: string) => Promise<void>;
  rewriteSlide: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    opts?: { content_lang?: string; user_instruction?: string; preview?: boolean; prompt_override?: string; prompt_system_override?: string }
  ) => Promise<string | undefined>;
  generateSlideImage: (strategyId: number, genId: number, slideNumber: number, data?: { template_id?: string }) => Promise<string | null>;
  deleteSlide: (strategyId: number, genId: number, slideNumber: number) => Promise<void>;
  insertSlide: (strategyId: number, genId: number, at_position: number) => Promise<void>;
  updateCarouselMeta: (strategyId: number, genId: number, data: { carousel_caption?: string; carousel_hashtags?: string }) => Promise<void>;
  rewriteCarouselCaption: (strategyId: number, genId: number, content_lang?: string) => Promise<void>;
  changeTemplate: (strategyId: number, genId: number, template_id: string, override?: { override_mode: 'one_off' | 'experiment' | 'adopt_incumbent'; decision_token: string }) => Promise<void>;
  updateSlideTemplate: (strategyId: number, genId: number, slideNumber: number, templateId: string | null, override?: { override_mode: 'one_off' | 'experiment' | 'adopt_incumbent'; decision_token: string }) => Promise<void>;
  updateCoverTemplate: (strategyId: number, genId: number, templateId: string | null) => Promise<void>;

  // Live overrides (Canva-style editor)
  updateSlideOverrides: (
    strategyId: number,
    genId: number,
    slideNumber: number,
    overrides: SlideOverrides,
  ) => Promise<void>;

  // Experiments
  experimentSuggestion: ExperimentSuggestion | null;
  experimentMatrix: ExperimentMatrix | null;
  experimentTimeline: ContentExperiment[];
  experimentLoading: boolean;
  fetchNextExperiment: (strategyId: number, contentType: string) => Promise<void>;
  fetchExperimentMatrix: (strategyId: number, contentType?: string) => Promise<void>;
  fetchExperimentTimeline: (strategyId: number, contentType?: string, limit?: number) => Promise<void>;

  // Topic Pool
  poolStats: PoolStats | null;
  poolImporting: boolean;
  poolImportError: string | null;
  setPoolStats: (stats: PoolStats | null) => void;
  setPoolImporting: (importing: boolean) => void;
  setPoolImportError: (error: string | null) => void;
  handlePoolImportSSE: (data: { strategy_id: number; niche: string; inserted: number; skipped: number; pending: number }) => void;

  // Week Plan
  _weekStrategyKey: string;
  weekSuggestions: WeekSlotSuggestion[];
  weekGenerations: WeekSlotGeneration[];
  weekPlanPhase: WeekPlanPhase;
  weekPlanProgress: Record<number, string>;
  weekRefreshing: boolean;
  weekConfirmError: string | null;
  weekConfirmSuccess: { count: number; nextScheduled: string } | null;
  weekMonday: string | null;
  weekTimeWindowMap: Record<string, string>;
  weekTheme: string | null;
  suggestWeekPlan: (strategyId: number, channelId?: number, contentLang?: string, theme?: string) => Promise<void>;
  generateWeekPlan: (strategyId: number, channelId?: number, contentLang?: string) => Promise<void>;
  confirmWeekPlan: (strategyId: number, channelId?: number) => Promise<void>;
  loadCurrentWeek: (strategyId: number, channelId?: number) => Promise<void>;
  regenerateWeekSlot: (strategyId: number, channelId: number | undefined, slotId: number, overrides?: { topic_angle?: string; tension_category?: string; hook_formula?: string }, rejectionData?: { reason?: string; presets?: string[] }) => Promise<void>;
  generateSingleSlot: (strategyId: number, channelId: number | undefined, slotId: number, contentLang?: string) => Promise<void>;
  swapTemplate: (strategyId: number, channelId: number | undefined, generationId: number) => Promise<void>;
  selectTemplate: (strategyId: number, channelId: number | undefined, generationId: number, templateId: string) => Promise<void>;
  updateWeekSuggestion: (slotId: number, patch: Partial<WeekSlotSuggestion>) => void;
  removeWeekSuggestion: (slotId: number) => void;
  handleWeekPlanSSE: (data: { slot_id: number; status: string; generation_id?: number; error?: string }) => Promise<void>;
  cancelSlotSchedule: (strategyId: number, publishLogId: number) => Promise<void>;
  deleteWeekGeneration: (strategyId: number, generationId: number) => Promise<void>;
  resetWeekPlan: () => void;
  clearWeekPlanServer: (strategyId: number, channelId?: number) => Promise<void>;

  // Engine Config
  engineConfig: EngineConfig | null;
  engineConfigLoading: boolean;
  fetchEngineConfig: (strategyId: number, channelId?: number) => Promise<void>;

  // Cockpit Scores
  cockpitScores: CockpitScores | null;
  cockpitScoresLoading: boolean;
  cockpitScoresSeq: number;
  fetchCockpitScores: (strategyId: number, channelId?: number) => Promise<void>;
}

export const createContentStudioSlice: StateCreator<ContentStudioSlice> = (set, get) => ({
  contentGenerations: [],
  contentTotal: 0,
  contentPresets: [],
  contentPromptPreview: null,
  promptInspector: null,
  contentRatingsSummary: null,
  contentGenerating: false,
  contentRefining: false,
  competitorPosts: [],
  postedContent: {},
  imageTemplates: [],
  carouselData: {},
  previewSlides: null,
  csFormState: {},
  cockpitLocks: {},
  viralHookCategories: [],
  viralHooksByCategory: {},
  loadCockpitLocks: async (strategyId) => {
    try {
      const res = await contentStudioApi.getCockpitLocks(strategyId);
      set((s) => ({ cockpitLocks: { ...s.cockpitLocks, [strategyId]: res } }));
    } catch (e) {
      // 401/403/etc · silent
    }
  },
  setCockpitLock: async (strategyId, field, value, hard) => {
    const res = await contentStudioApi.setCockpitLock(strategyId, field, value, hard);
    set((s) => ({
      cockpitLocks: {
        ...s.cockpitLocks,
        [strategyId]: { ...(s.cockpitLocks[strategyId] || {}), [field]: res },
      },
    }));
  },
  clearCockpitLock: async (strategyId, field) => {
    await contentStudioApi.clearCockpitLock(strategyId, field);
    set((s) => {
      const next = { ...(s.cockpitLocks[strategyId] || {}) };
      delete next[field];
      return { cockpitLocks: { ...s.cockpitLocks, [strategyId]: next } };
    });
  },

  layer12Sources: {},
  layer12Loading: {},
  loadLayer12Sources: async (strategyId) => {
    set((s) => ({ layer12Loading: { ...s.layer12Loading, [strategyId]: true } }));
    try {
      const res = await contentStudioApi.getLayer12Sources(strategyId);
      set((s) => ({
        layer12Sources: { ...s.layer12Sources, [strategyId]: res.sources || [] },
        layer12Loading: { ...s.layer12Loading, [strategyId]: false },
      }));
    } catch {
      set((s) => ({ layer12Loading: { ...s.layer12Loading, [strategyId]: false } }));
    }
  },
  updateLayer12Sources: async (strategyId, sources) => {
    // Optimistic update; rollback on error.
    const previous = get().layer12Sources[strategyId] || [];
    set((s) => ({ layer12Sources: { ...s.layer12Sources, [strategyId]: sources } }));
    try {
      const res = await contentStudioApi.updateLayer12Sources(strategyId, sources);
      set((s) => ({
        layer12Sources: { ...s.layer12Sources, [strategyId]: res.sources || [] },
      }));
    } catch {
      set((s) => ({ layer12Sources: { ...s.layer12Sources, [strategyId]: previous } }));
    }
  },
  toggleLayer12Source: async (strategyId, source) => {
    const current = get().layer12Sources[strategyId] || [];
    const exists = current.some(s => s.type === source.type && String(s.id) === String(source.id));
    const next = exists
      ? current.filter(s => !(s.type === source.type && String(s.id) === String(source.id)))
      : [...current, source];
    await get().updateLayer12Sources(strategyId, next);
  },

  poolNextTopic: {},
  poolNextLoading: {},
  loadPoolNextTopic: async (strategyId) => {
    set((s) => ({ poolNextLoading: { ...s.poolNextLoading, [strategyId]: true } }));
    try {
      const res = await contentStudioApi.getNextPoolTopic(strategyId);
      set((s) => ({
        poolNextTopic: { ...s.poolNextTopic, [strategyId]: res },
        poolNextLoading: { ...s.poolNextLoading, [strategyId]: false },
      }));
    } catch {
      set((s) => ({
        poolNextTopic: { ...s.poolNextTopic, [strategyId]: null },
        poolNextLoading: { ...s.poolNextLoading, [strategyId]: false },
      }));
    }
  },
  loadViralHookCategories: async (strategyId) => {
    try {
      const res = await contentStudioApi.getViralHookCategories(strategyId);
      set({ viralHookCategories: res });
    } catch (e) {
      /* silent */
    }
  },
  loadViralHooks: async (strategyId, category) => {
    try {
      const res = await contentStudioApi.getViralHooks(strategyId, { category });
      set((s) => ({
        viralHooksByCategory: { ...s.viralHooksByCategory, [category]: res },
      }));
    } catch (e) {
      /* silent */
    }
  },
  csSubTab: 'create',
  csSelectedStrategyId: null,
  csCurrentGeneration: {},

  // Multi-platform
  multiPlatformGenerations: [],
  multiPlatformLoading: false,

  // Batch
  batchTopics: [],
  batchGenerations: [],
  batchErrors: [],
  batchGenerating: false,
  suggestingTopics: false,

  // Content Formats
  contentFormats: [],
  templateRankings: [],
  selectedFormat: null,
  rankingLoading: false,

  // Content Plan
  activePlan: null,
  planSlots: [],
  todayTimeline: [],
  planLoading: false,

  // Format Performance
  formatPerformance: [],
  performanceSuggestions: [],
  aiAnalysis: null,
  analysisLoading: false,

  // Critic Stats (Phase 2)
  criticStats: null,
  criticStatsLoading: false,
  criticStatsError: null,

  // Experiments
  experimentSuggestion: null,
  experimentMatrix: null,
  experimentTimeline: [],
  experimentLoading: false,

  // Engine Config
  engineConfig: null,
  engineConfigLoading: false,

  // Cockpit Scores
  cockpitScores: null,
  cockpitScoresLoading: false,
  cockpitScoresSeq: 0,

  // Topic Pool
  poolStats: null,
  poolImporting: false,
  poolImportError: null,

  // Week Plan
  _weekStrategyKey: '',
  weekSuggestions: [],
  weekGenerations: [],
  weekPlanPhase: 'idle',
  weekPlanProgress: {},
  weekRefreshing: false,
  weekConfirmError: null,
  weekConfirmSuccess: null,
  weekMonday: null,
  weekTimeWindowMap: { morning: '09:00', afternoon: '14:00', evening: '19:00' },
  weekTheme: null,

  setPreviewSlides: (slides) => set({ previewSlides: slides }),

  setCsFormState: (strategyId, form) =>
    set((s) => {
      // Derive default content language from the strategy's account_language.
      // Prefer activeStrategy for O(1) lookup when ids match; otherwise scan the list.
      // Cross-slice state lives in strategySlice, so we access the full store via useStore.getState().
      const rootState = useStore.getState();
      const strategyForLang =
        rootState.activeStrategy && rootState.activeStrategy.id === strategyId
          ? rootState.activeStrategy
          : rootState.strategies.find((st: { id: number }) => st.id === strategyId);
      const rawLang = strategyForLang?.account_language;
      const derivedLang: 'en' | 'de' | 'es' =
        rawLang === 'en' || rawLang === 'de' || rawLang === 'es' ? rawLang : 'en';
      const defaults: CsFormState = {
        contentType: 'carousel_concept',
        contentFormat: 'image',
        contentTypePerFormat: { image: 'carousel_concept', video: 'video_drehplan' },
        model: 'haiku',
        contentLang: derivedLang,
        customRules: '',
        presetId: '',
        experiments: ['original_mode', 'rich_context', 'sharp_rules', 'guided_gen', 'account_context'],
        topicAngle: '',
        tensionCategory: null,
        hookFormula: null,
        viralHookCategory: null,
        viralHookId: null,
        autoSequence: true,
        aggressiveness: SLIDER_DEFAULTS.aggressiveness,
        aspectFocus: SLIDER_DEFAULTS.aspectFocus,
        toneFormality: SLIDER_DEFAULTS.toneFormality,
        mehrwert: SLIDER_DEFAULTS.mehrwert,
        metricAccumulation: SLIDER_DEFAULTS.metricAccumulation,
        documentIds: [],
        excludedBookIds: [],
        includedBookIds: [],
      };
      const current: CsFormState = s.csFormState[strategyId] || defaults;
      const next = { ...current, ...form };

      // When format changes → restore saved content type for that format
      if (form.contentFormat && form.contentFormat !== current.contentFormat) {
        next.contentType = next.contentTypePerFormat[form.contentFormat];
      }
      // When content type changes → persist it under current format
      if (form.contentType && form.contentType !== current.contentType) {
        next.contentTypePerFormat = {
          ...next.contentTypePerFormat,
          [next.contentFormat]: form.contentType,
        };
      }

      return { csFormState: { ...s.csFormState, [strategyId]: next } };
    }),

  setCsSubTab: (tab: 'create' | 'history' | 'analytics' | 'plan' | 'batch' | 'queue') => set({ csSubTab: tab }),

  setCsSelectedStrategyId: (id) =>
    set((s) => {
      // Atomic init: ensure csFormState[id] exists immediately on selection.
      // Prevents the component from rendering against an ephemeral fallback
      // object · fallback array refs (experiments, documentIds) would otherwise
      // change every render and re-trigger useEffects → React error #185.
      if (id == null || s.csFormState[id]) {
        return { csSelectedStrategyId: id };
      }
      const rootState = useStore.getState();
      const strategyForLang =
        rootState.activeStrategy && rootState.activeStrategy.id === id
          ? rootState.activeStrategy
          : rootState.strategies.find((st: { id: number }) => st.id === id);
      const rawLang = strategyForLang?.account_language;
      const derivedLang: 'en' | 'de' | 'es' =
        rawLang === 'en' || rawLang === 'de' || rawLang === 'es' ? rawLang : 'en';
      const initial: CsFormState = {
        contentType: 'carousel_concept',
        contentFormat: 'image',
        contentTypePerFormat: { image: 'carousel_concept', video: 'video_drehplan' },
        model: 'haiku',
        contentLang: derivedLang,
        customRules: '',
        presetId: '',
        experiments: ['original_mode', 'rich_context', 'sharp_rules', 'guided_gen', 'account_context'],
        topicAngle: '',
        tensionCategory: null,
        hookFormula: null,
        viralHookCategory: null,
        viralHookId: null,
        autoSequence: true,
        aggressiveness: SLIDER_DEFAULTS.aggressiveness,
        aspectFocus: SLIDER_DEFAULTS.aspectFocus,
        toneFormality: SLIDER_DEFAULTS.toneFormality,
        mehrwert: SLIDER_DEFAULTS.mehrwert,
        metricAccumulation: SLIDER_DEFAULTS.metricAccumulation,
        documentIds: [],
        excludedBookIds: [],
        includedBookIds: [],
      };
      return {
        csSelectedStrategyId: id,
        csFormState: { ...s.csFormState, [id]: initial },
      };
    }),

  setCsCurrentGeneration: (strategyId, gen) =>
    set((s) => ({ csCurrentGeneration: { ...s.csCurrentGeneration, [strategyId]: gen } })),

  generateContent: async (strategyId, data) => {
    set({ contentGenerating: true });
    try {
      const form = get().csFormState[strategyId];
      const enrichedData = {
        ...data,
        ...(form?.tensionCategory != null ? { tension_category: form.tensionCategory } : {}),
        ...(form?.hookFormula != null ? { hook_formula: form.hookFormula } : {}),
        ...(form?.viralHookCategory != null ? { viral_hook_category: form.viralHookCategory } : {}),
        ...(form?.viralHookId != null ? { viral_hook_id: form.viralHookId } : {}),
        ...(form?.documentIds && form.documentIds.length > 0 ? { document_ids: form.documentIds } : {}),
        ...(form?.includedBookIds ? { included_book_ids: form.includedBookIds } : {}),
        ...(form?.metricAccumulation ? { metric_accumulation: true } : {}),
        auto_sequence: form?.autoSequence ?? true,
      };
      const result = await contentStudioApi.generate(strategyId, enrichedData);
      // Merge enriched stat_variants into the primary generation so the component can access them
      const primaryGen = result.stat_variants
        ? { ...result.generation, stat_variants: result.stat_variants }
        : result.generation;
      const allNew = [primaryGen, ...(result.additional_generations ?? [])];
      set((s) => ({
        contentGenerations: [...allNew, ...s.contentGenerations],
      }));
      return primaryGen;
    } finally {
      set({ contentGenerating: false });
    }
  },

  refineContent: async (strategyId, genId, feedback, model) => {
    set({ contentRefining: true });
    try {
      const result = await contentStudioApi.refine(strategyId, genId, feedback, model);
      const gen = result.generation;
      if (gen) {
        set((s) => ({
          contentGenerations: [gen, ...s.contentGenerations],
        }));
      }
      return gen ?? null;
    } finally {
      set({ contentRefining: false });
    }
  },

  regenerateContent: async (strategyId, genId, model, opts) => {
    set({ contentGenerating: true });
    try {
      const result = await contentStudioApi.regenerate(strategyId, genId, {
        model,
        preserveTemplate: opts?.preserveTemplate,
      });
      set((s) => ({
        contentGenerations: [result.generation, ...s.contentGenerations],
      }));
      return result.generation;
    } finally {
      set({ contentGenerating: false });
    }
  },

  previewContentPrompt: async (strategyId, data) => {
    const result = await contentStudioApi.previewPrompt(strategyId, data);
    set({ contentPromptPreview: result });
  },

  clearContentPromptPreview: () => set({ contentPromptPreview: null }),
  openPromptInspector: (cfg) => set({ promptInspector: cfg }),
  closePromptInspector: () => set({ promptInspector: null }),

  fetchGenerations: async (strategyId, params) => {
    const result = await contentStudioApi.listGenerations(strategyId, params);
    set({ contentGenerations: result.items, contentTotal: result.total });
  },

  toggleStarGeneration: async (strategyId, genId, starred) => {
    const updated = await contentStudioApi.updateGeneration(strategyId, genId, {
      starred,
    } as Partial<ContentGeneration>);
    set((s) => ({
      contentGenerations: s.contentGenerations.map((g) => (g.id === genId ? updated : g)),
    }));
  },

  deleteGeneration: async (strategyId, genId) => {
    await contentStudioApi.deleteGeneration(strategyId, genId);
    set((s) => ({
      contentGenerations: s.contentGenerations.filter((g) => g.id !== genId),
    }));
  },

  rateContent: async (strategyId, genId, rating, notes) => {
    await contentStudioApi.rate(strategyId, genId, rating, notes);
  },

  fetchContentPresets: async (strategyId) => {
    const presets = await contentStudioApi.getPresets(strategyId);
    set({ contentPresets: presets });
  },

  createContentPreset: async (strategyId, data) => {
    await contentStudioApi.createPreset(strategyId, data);
    await get().fetchContentPresets(strategyId);
  },

  deleteContentPreset: async (strategyId, presetId) => {
    await contentStudioApi.deletePreset(strategyId, presetId);
    await get().fetchContentPresets(strategyId);
  },

  fetchRatingsSummary: async (strategyId) => {
    const summary = await contentStudioApi.getRatingsSummary(strategyId);
    set({ contentRatingsSummary: summary });
  },

  fetchCompetitorPosts: async (strategyId, limit = 5) => {
    const result = await contentStudioApi.getCompetitorPosts(strategyId, limit);
    set({ competitorPosts: result.posts });
  },

  markAsPosted: async (strategyId, genId, post_url, scrape_intervals) => {
    const posted = await contentStudioApi.markPosted(strategyId, genId, post_url, scrape_intervals);
    set((s) => ({ postedContent: { ...s.postedContent, [genId]: posted } }));
  },

  fetchEngagement: async (strategyId, genId) => {
    const posted = await contentStudioApi.getEngagement(strategyId, genId);
    if (posted) {
      set((s) => ({ postedContent: { ...s.postedContent, [genId]: posted } }));
    }
  },

  fetchImageTemplates: async (force?: boolean) => {
    if (!force && get().imageTemplates.length > 0) return;
    const templates = await contentStudioApi.listTemplates();
    set({ imageTemplates: templates });
  },

  // Content Format actions
  setContentFormats: (formats) => set({ contentFormats: formats }),
  setTemplateRankings: (rankings) => set({ templateRankings: rankings }),
  setSelectedFormat: (format) => set({ selectedFormat: format }),
  setRankingLoading: (loading) => set({ rankingLoading: loading }),

  fetchContentFormats: async () => {
    try {
      const result = await contentStudioApi.listContentFormats();
      set({ contentFormats: result.formats });
    } catch {
      // ignore
    }
  },

  rankTemplatesForText: async (strategyId, text, contentFormat) => {
    set({ rankingLoading: true });
    try {
      const result = await contentStudioApi.rankTemplates(strategyId, text, contentFormat);
      set({ templateRankings: result.rankings, rankingLoading: false });
    } catch {
      set({ rankingLoading: false });
    }
  },

  // Content Plan actions
  fetchPlan: async (strategyId, channelId) => {
    const newKey = `${strategyId}:${channelId ?? 0}`;
    const isSwitch = get()._weekStrategyKey !== newKey;
    set({
      planLoading: true,
      ...(isSwitch ? {
        weekSuggestions: [],
        weekGenerations: [],
        weekPlanPhase: 'idle' as WeekPlanPhase,
        weekPlanProgress: {},
        weekConfirmError: null,
        weekConfirmSuccess: null,
        _weekStrategyKey: newKey,
      } : {}),
    });
    try {
      const result = await contentStudioApi.getPlan(strategyId, channelId);
      set({ activePlan: result.plan, planSlots: result.slots, planLoading: false });
    } catch {
      set({ planLoading: false });
    }
  },

  createPlan: async (strategyId, data, channelId) => {
    const payload = channelId != null ? { ...data, channel_id: channelId } : data;
    const result = await contentStudioApi.createPlan(strategyId, payload);
    set({ activePlan: result.plan, planSlots: result.slots });
  },

  updatePlan: async (strategyId, planId, data) => {
    const result = await contentStudioApi.updatePlan(strategyId, planId, data);
    set({ activePlan: result.plan });
  },

  deletePlan: async (strategyId, planId) => {
    await contentStudioApi.deletePlan(strategyId, planId);
    set({ activePlan: null, planSlots: [] });
  },

  fetchTodayTimeline: async (strategyId) => {
    try {
      const result = await contentStudioApi.getTodayTimeline(strategyId);
      set({ todayTimeline: result.slots });
    } catch {
      // ignore
    }
  },

  addPlanSlot: async (strategyId, planId, data) => {
    const result = await contentStudioApi.addSlot(strategyId, planId, data);
    set({ planSlots: result.slots });
  },

  updatePlanSlot: async (strategyId, planId, slotId, data) => {
    const result = await contentStudioApi.updateSlot(strategyId, planId, slotId, data);
    set({ planSlots: result.slots });
  },

  deletePlanSlot: async (strategyId, planId, slotId) => {
    const result = await contentStudioApi.deleteSlot(strategyId, planId, slotId);
    set({ planSlots: result.slots });
  },

  revertPlanSlot: async (strategyId, planId, slotId) => {
    const result = await contentStudioApi.revertSlot(strategyId, planId, slotId);
    set({ planSlots: result.slots });
  },

  // Format Performance actions
  fetchFormatPerformance: async (strategyId) => {
    try {
      const result = await contentStudioApi.getFormatPerformance(strategyId);
      set({ formatPerformance: result.performance, performanceSuggestions: result.suggestions });
    } catch {
      // ignore
    }
  },

  fetchCriticStats: async (strategyId, days = 30) => {
    set({ criticStats: null, criticStatsLoading: true, criticStatsError: null });
    try {
      const stats = await contentStudioApi.getCriticStats(strategyId, days);
      set({ criticStats: stats, criticStatsLoading: false });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load critic stats';
      set({ criticStatsLoading: false, criticStatsError: message });
    }
  },

  fetchAIAnalysis: async (strategyId) => {
    set({ analysisLoading: true });
    try {
      const result = await contentStudioApi.getAIAnalysis(strategyId);
      set({ aiAnalysis: result.analysis, analysisLoading: false });
    } catch {
      set({ analysisLoading: false });
    }
  },

  runAutoAdjust: async (strategyId) => {
    try {
      const result = await contentStudioApi.runAutoAdjust(strategyId);
      set({ planSlots: result.slots });
    } catch {
      // ignore
    }
  },

  fetchCarouselSlides: async (strategyId, genId) => {
    const data = await contentStudioApi.getCarouselSlides(strategyId, genId);
    set((s) => ({ carouselData: { ...s.carouselData, [genId]: data } }));
  },

  updateSlideText: async (strategyId, genId, slideNumber, text) => {
    await contentStudioApi.updateSlideText(strategyId, genId, slideNumber, text);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return {
        carouselData: {
          ...s.carouselData,
          [genId]: {
            ...d,
            slides: d.slides.map((sl) =>
              sl.slide_number === slideNumber ? { ...sl, slide_text: text } : sl
            ),
          },
        },
      };
    });
  },

  rewriteSlide: async (strategyId, genId, slideNumber, opts) => {
    const result = await contentStudioApi.rewriteSlide(strategyId, genId, slideNumber, opts);
    const newText = result.slide_text;
    if (newText) {
      set((s) => {
        const d = s.carouselData[genId];
        if (!d) return {};
        return {
          carouselData: {
            ...s.carouselData,
            [genId]: {
              ...d,
              slides: d.slides.map((sl) =>
                sl.slide_number === slideNumber ? { ...sl, slide_text: newText } : sl
              ),
            },
          },
        };
      });
    }
    return newText;
  },

  generateSlideImage: async (strategyId, genId, slideNumber, data) => {
    try {
      const result = await contentStudioApi.generateSlideImage(strategyId, genId, slideNumber, data || {});
      const imagePath = result.image_path;
      set((s) => {
        const d = s.carouselData[genId];
        if (!d) return {};
        return {
          carouselData: {
            ...s.carouselData,
            [genId]: {
              ...d,
              slides: d.slides.map((sl) =>
                sl.slide_number === slideNumber ? { ...sl, image_path: imagePath } : sl
              ),
            },
          },
        };
      });
      return imagePath;
    } catch {
      return null;
    }
    // Note: overflow_warning and alternative_templates are available on the raw API result
    // but not persisted in store - they are handled transiently in the component
  },

  deleteSlide: async (strategyId, genId, slideNumber) => {
    await contentStudioApi.deleteSlide(strategyId, genId, slideNumber);
    await get().fetchCarouselSlides(strategyId, genId);
  },

  insertSlide: async (strategyId, genId, at_position) => {
    const result = await contentStudioApi.insertSlide(strategyId, genId, at_position);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return { carouselData: { ...s.carouselData, [genId]: { ...d, slides: result.slides } } };
    });
  },

  updateCarouselMeta: async (strategyId, genId, data) => {
    await contentStudioApi.updateCarouselMeta(strategyId, genId, data);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return { carouselData: { ...s.carouselData, [genId]: { ...d, ...data } } };
    });
  },

  rewriteCarouselCaption: async (strategyId, genId, content_lang) => {
    const result = await contentStudioApi.rewriteCarouselCaption(strategyId, genId, content_lang);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return {
        carouselData: {
          ...s.carouselData,
          [genId]: { ...d, carousel_caption: result.caption ?? d.carousel_caption, carousel_hashtags: result.hashtags ?? d.carousel_hashtags },
        },
      };
    });
  },

  changeTemplate: async (strategyId, genId, template_id, override) => {
    await contentStudioApi.changeTemplate(strategyId, genId, template_id, override);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return {
        carouselData: {
          ...s.carouselData,
          [genId]: { ...d, template_id, slides: d.slides.map((sl) => ({ ...sl, image_path: null })) },
        },
      };
    });
  },

  updateSlideTemplate: async (strategyId, genId, slideNumber, templateId, override) => {
    await contentStudioApi.updateSlideTemplate(strategyId, genId, slideNumber, templateId, override);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return {
        carouselData: {
          ...s.carouselData,
          [genId]: {
            ...d,
            slides: d.slides.map((sl) => ({ ...sl, template_id: templateId })),
          },
        },
      };
    });
  },

  updateCoverTemplate: async (strategyId, genId, templateId) => {
    await contentStudioApi.updateCoverTemplate(strategyId, genId, templateId);
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return {
        carouselData: {
          ...s.carouselData,
          [genId]: {
            ...d,
            cover_template_id: templateId,
            slides: d.slides.map((sl) => sl.slide_number === 1 ? { ...sl, is_stale: 1 } : sl),
          },
        },
      };
    });
  },

  updateSlideOverrides: async (strategyId, genId, slideNumber, overrides) => {
    // Optimistic local apply · UI updates immediately.
    set((s) => {
      const d = s.carouselData[genId];
      if (!d) return {};
      return {
        carouselData: {
          ...s.carouselData,
          [genId]: {
            ...d,
            slides: d.slides.map((sl) =>
              sl.slide_number === slideNumber ? { ...sl, slide_overrides: overrides } : sl,
            ),
          },
        },
      };
    });
    try {
      const resp = await contentStudioApi.updateSlideOverrides(strategyId, genId, slideNumber, overrides);
      // Adopt server-normalized values (may differ from local if clamped).
      set((s) => {
        const d = s.carouselData[genId];
        if (!d) return {};
        return {
          carouselData: {
            ...s.carouselData,
            [genId]: {
              ...d,
              slides: d.slides.map((sl) =>
                sl.slide_number === slideNumber
                  ? { ...sl, slide_overrides: resp.slide_overrides, is_stale: 1 }
                  : sl,
              ),
            },
          },
        };
      });
    } catch (err) {
      // Don't revert on abort · a newer request is already in flight.
      if (err instanceof Error && err.name === 'AbortError') return;
      throw err;
    }
  },

  // Experiment actions
  fetchNextExperiment: async (strategyId, contentType) => {
    set({ experimentLoading: true });
    try {
      const result = await contentStudioApi.getNextExperiment(strategyId, contentType);
      set({ experimentSuggestion: result, experimentLoading: false });
    } catch {
      set({ experimentLoading: false });
    }
  },

  fetchExperimentMatrix: async (strategyId, contentType) => {
    try {
      const result = await contentStudioApi.getExperimentMatrix(strategyId, contentType);
      set({ experimentMatrix: result });
    } catch {
      // ignore
    }
  },

  fetchExperimentTimeline: async (strategyId, contentType, limit) => {
    try {
      const result = await contentStudioApi.getExperiments(strategyId, {
        limit: limit ?? 10,
        content_type: contentType,
      });
      set({ experimentTimeline: result.items });
    } catch {
      // ignore
    }
  },

  generateMultiPlatform: async (strategyId, data) => {
    set({ multiPlatformLoading: true });
    try {
      const result = await contentStudioApi.generateMultiPlatform(strategyId, data);
      set((s) => ({
        multiPlatformGenerations: result.generations,
        contentGenerations: [...result.generations, ...s.contentGenerations],
        multiPlatformLoading: false,
      }));
      return result.generations;
    } catch {
      set({ multiPlatformLoading: false });
      return [];
    }
  },

  setBatchTopics: (topics) => set({ batchTopics: topics }),

  suggestBatchTopics: async (strategyId, opts) => {
    set({ suggestingTopics: true });
    try {
      const result = await contentStudioApi.suggestBatchTopics(strategyId, opts);
      set({ batchTopics: result.topics, suggestingTopics: false });
    } catch {
      set({ suggestingTopics: false });
    }
  },

  batchGenerate: async (strategyId, data) => {
    set({ batchGenerating: true, batchErrors: [] });
    try {
      const result = await contentStudioApi.batchGenerate(strategyId, data);
      set((s) => ({
        batchGenerations: result.generations,
        contentGenerations: [...result.generations, ...s.contentGenerations],
        batchErrors: result.errors,
        batchGenerating: false,
      }));
      return result.generations;
    } catch {
      set({ batchGenerating: false });
      return [];
    }
  },

  // Topic Pool actions
  setPoolStats: (stats) => set({ poolStats: stats }),
  setPoolImporting: (importing) => set({ poolImporting: importing }),
  setPoolImportError: (error) => set({ poolImportError: error }),

  handlePoolImportSSE: (data) => {
    const { activeStrategyId } = useStore.getState();
    if (data.strategy_id !== activeStrategyId) return;
    contentStudioApi.getPoolStats(data.strategy_id)
      .then((stats) => set({ poolStats: stats }))
      .catch(() => {});
  },

  // Week Plan actions
  suggestWeekPlan: async (strategyId, channelId, contentLang, theme) => {
    // Reset stale review state when re-running planning (theme change or refresh)
    set({
      weekPlanPhase: 'suggesting',
      weekSuggestions: [],
      weekGenerations: [],
      weekPlanProgress: {},
      weekTheme: null,
    });
    try {
      // Clean up non-scheduled generations on backend before suggesting
      await contentStudioApi.resetWeekPlan(strategyId, { channel_id: channelId ?? 0 });
      const params: { channel_id?: number; content_lang?: string; theme?: string } = {};
      if (channelId != null) params.channel_id = channelId;
      if (contentLang) params.content_lang = contentLang;
      // Normalize theme client-side (server normalizes again as defense-in-depth)
      const normalizedTheme = theme ? theme.trim().replace(/\s+/g, ' ').slice(0, 200) : '';
      if (normalizedTheme) params.theme = normalizedTheme;
      const result = await contentStudioApi.suggestWeekPlan(strategyId, params);
      set({
        weekSuggestions: result.suggestions,
        weekPlanPhase: 'review',
        weekTheme: result.theme ?? (normalizedTheme || null),
      });
    } catch {
      set({ weekPlanPhase: 'idle' });
    }
  },

  generateWeekPlan: async (strategyId, channelId, contentLang) => {
    const allSuggestions = get().weekSuggestions.filter((s) => !s.skipped);
    const existingGenerations = get().weekGenerations;
    // Filter out slots that already have a done generation
    const doneSlotIds = new Set(
      existingGenerations.filter((g) => g.status === 'done').map((g) => g.slot_id)
    );
    const confirmedSlots = allSuggestions.filter((s) => !doneSlotIds.has(s.slot_id));

    if (confirmedSlots.length === 0) {
      // All slots already generated · nothing to do
      set({ weekPlanPhase: 'done' });
      return;
    }

    // For newly-to-generate slots: mark as pending; preserve existing done entries
    const newPendingGenerations: WeekSlotGeneration[] = confirmedSlots.map((s) => ({
      slot_id: s.slot_id,
      generation_id: null,
      template_id: null,
      output_text: '',
      status: 'pending',
      error: null,
    }));
    set((state) => ({
      weekPlanPhase: 'generating',
      weekGenerations: [
        ...state.weekGenerations.filter((g) => doneSlotIds.has(g.slot_id)),
        ...newPendingGenerations,
      ],
      weekPlanProgress: {},
    }));
    try {
      const params: { channel_id?: number; content_lang?: string; confirmed_slots: WeekSlotSuggestion[] } = {
        confirmed_slots: confirmedSlots,
      };
      if (channelId != null) params.channel_id = channelId;
      if (contentLang) params.content_lang = contentLang;
      const result = await contentStudioApi.generateWeekPlan(strategyId, params);
      // Merge new results with existing done generations
      set((state) => ({
        weekGenerations: [
          ...state.weekGenerations.filter((g) => doneSlotIds.has(g.slot_id)),
          ...result.results,
        ],
        weekPlanPhase: 'done',
      }));
    } catch {
      set({ weekPlanPhase: 'review' });
    }
  },

  confirmWeekPlan: async (strategyId, channelId) => {
    set({ weekConfirmError: null, weekConfirmSuccess: null });
    try {
      const doneGenerations = get().weekGenerations.filter((g) => g.status === 'done' && g.generation_id != null);
      const generation_ids = doneGenerations.map((g) => g.generation_id as number);
      const params: { channel_id?: number; generation_ids: number[] } = { generation_ids };
      if (channelId != null) params.channel_id = channelId;
      const response = await contentStudioApi.confirmWeekPlan(strategyId, params);
      const results = response?.scheduled ?? [];
      if (Array.isArray(results)) {
        const errorsByGenId = new Map(
          results
            .filter((r): r is { generation_id: number; error: string; message?: string } => 'error' in r)
            .map((r) => [r.generation_id, r.message ?? r.error ?? 'Fehler'])
        );
        if (errorsByGenId.size > 0) {
          set((s) => ({
            weekGenerations: s.weekGenerations.map((g) => {
              if (g.generation_id != null && errorsByGenId.has(g.generation_id)) {
                return { ...g, status: 'error' as const, error: errorsByGenId.get(g.generation_id) ?? 'Fehler' };
              }
              return g;
            }),
            weekConfirmError: `${errorsByGenId.size} Post(s) konnten nicht geplant werden.`,
          }));
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const skippedResults = results.filter((r: any) => 'skipped' in r);
        if (skippedResults.length > 0 && errorsByGenId.size === 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const pastCount = skippedResults.filter((r: any) => r.reason === 'past_slot').length;
          if (pastCount > 0) {
            set({ weekConfirmError: `${pastCount} Post(s) uebersprungen: Slot liegt in der Vergangenheit.` });
          }
        }
        const successResults = results.filter(
          (r): r is import('../types').WeekPlanConfirmResult => !('error' in r) && 'scheduled_at' in r
        );
        if (successResults.length > 0) {
          const scheduledDates = successResults.map((r) => r.scheduled_at).sort();
          const earliest = scheduledDates[0];
          const nextScheduled = earliest
            ? new Date(earliest).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            : '';
          set({ weekConfirmSuccess: { count: successResults.length, nextScheduled } });
          setTimeout(() => {
            set({ weekConfirmSuccess: null });
          }, 8000);
          // Re-fetch server state: backend now filters out confirmed generations,
          // so this naturally resolves to idle (all scheduled) or partial (some failed).
          await get().loadCurrentWeek(strategyId, channelId);
        }
      }
    } catch (err) {
      set({ weekConfirmError: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.' });
    }
  },

  loadCurrentWeek: async (strategyId, channelId) => {
    const requestKey = `${strategyId}:${channelId ?? 0}`;
    set({ weekRefreshing: true });
    try {
      const result = await contentStudioApi.getCurrentWeek(strategyId, { channel_id: channelId ?? 0 });
      // Ignore stale response if user switched strategy during fetch
      if (get()._weekStrategyKey !== requestKey) return;
      // Don't override state if an active planning phase is in progress (race condition guard)
      if (get().weekPlanPhase !== 'idle') return;
      const suggestions = result.suggestions ?? [];
      const generations = result.generations ?? [];
      if (result.week_monday) set({ weekMonday: result.week_monday });
      if (result.time_window_map) set({ weekTimeWindowMap: result.time_window_map });
      if (generations.length > 0) {
        // Build synthetic suggestions from generation slot data so the grid
        // can place them in the correct day/time_window cell.
        const existingSuggestionSlots = new Set(suggestions.map((s: WeekSlotSuggestion) => s.slot_id));
        const syntheticSuggestions: WeekSlotSuggestion[] = generations
          .filter((g): g is typeof g & { day: string } => !existingSuggestionSlots.has(g.slot_id) && !!g.day)
          .map((g) => ({
            slot_id: g.slot_id,
            day: g.day,
            time_window: g.time_window || 'morning',
            content_format: g.content_format ?? 'carousel',
            content_type: 'carousel_concept',
            topic_angle: '',
            tension_category: '',
            hook_formula: '',
            source: 'persisted',
            is_schedulable: !isDayPast(g.day, get().weekMonday),
          }));
        set({
          weekSuggestions: [...suggestions, ...syntheticSuggestions],
          weekGenerations: generations.map((g) => ({
            slot_id: g.slot_id,
            generation_id: g.generation_id,
            template_id: g.template_id,
            output_text: g.output_text,
            display_text: g.display_text,
            slide1_text: g.slide1_text,
            slide1_image_path: g.slide1_image_path,
            slides: g.slides,
            status: 'done' as const,
            error: null,
            is_scheduled: g.is_scheduled,
            publish_log_id: g.publish_log_id,
            schedule_status: g.schedule_status,
          })),
          weekPlanPhase: 'done',
        });
      } else {
        set({ weekPlanPhase: 'idle' });
      }
    } catch {
      // silently ignore · no persisted week plan is a normal state
    } finally {
      set({ weekRefreshing: false });
    }
  },

  regenerateWeekSlot: async (strategyId, channelId, slotId, overrides, rejectionData) => {
    set((s) => ({
      weekGenerations: s.weekGenerations.map((g) =>
        g.slot_id === slotId ? { ...g, status: 'generating' as const, error: null } : g
      ),
      weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'generating' },
    }));
    try {
      const params: { channel_id?: number; slot_id: number; overrides?: { topic_angle?: string; tension_category?: string; hook_formula?: string }; rejection_reason?: string; rejection_presets?: string[] } = { slot_id: slotId };
      if (channelId != null) params.channel_id = channelId;
      if (overrides) params.overrides = overrides;
      if (rejectionData?.reason) params.rejection_reason = rejectionData.reason;
      if (rejectionData?.presets?.length) params.rejection_presets = rejectionData.presets;
      const result = await contentStudioApi.regenerateWeekSlot(strategyId, params);
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.slot_id === slotId ? result.result : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'done' },
      }));
    } catch {
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.slot_id === slotId ? { ...g, status: 'error' as const, error: 'Regeneration failed' } : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'error' },
      }));
    }
  },

  generateSingleSlot: async (strategyId, channelId, slotId, contentLang) => {
    const suggestion = get().weekSuggestions.find((s) => s.slot_id === slotId);
    if (!suggestion) return;

    // Upsert generating entry in weekGenerations
    set((s) => {
      const exists = s.weekGenerations.some((g) => g.slot_id === slotId);
      const generatingEntry: WeekSlotGeneration = {
        slot_id: slotId,
        generation_id: null,
        template_id: null,
        output_text: '',
        display_text: '',
        status: 'generating',
        error: null,
      };
      return {
        weekGenerations: exists
          ? s.weekGenerations.map((g) => g.slot_id === slotId ? generatingEntry : g)
          : [...s.weekGenerations, generatingEntry],
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'generating' },
      };
    });

    try {
      const params: { channel_id?: number; content_lang?: string; confirmed_slots: WeekSlotSuggestion[] } = {
        confirmed_slots: [suggestion],
      };
      if (channelId != null) params.channel_id = channelId;
      if (contentLang) params.content_lang = contentLang;
      const result = await contentStudioApi.generateWeekPlan(strategyId, params);
      const slotResult = result.results.find((r) => r.slot_id === slotId) ?? result.results[0];
      if (slotResult) {
        set((s) => ({
          weekGenerations: s.weekGenerations.map((g) => g.slot_id === slotId ? { ...slotResult, status: slotResult.status as WeekSlotGeneration['status'] } : g),
          weekPlanProgress: { ...s.weekPlanProgress, [slotId]: slotResult.status },
        }));
      }
    } catch {
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.slot_id === slotId ? { ...g, status: 'error' as const, error: 'Generation failed' } : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'error' },
      }));
    }
  },

  swapTemplate: async (strategyId, channelId, generationId) => {
    // Find slotId for this generation
    const gen = get().weekGenerations.find((g) => g.generation_id === generationId);
    if (!gen) return;
    const slotId = gen.slot_id;

    set((s) => ({
      weekGenerations: s.weekGenerations.map((g) =>
        g.generation_id === generationId ? { ...g, status: 'generating' as const, error: null } : g
      ),
      weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'generating' },
    }));

    try {
      const result = await contentStudioApi.swapTemplate(strategyId, {
        channel_id: channelId ?? 0,
        generation_id: generationId,
      });
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.generation_id === generationId
            ? { ...g, ...result, status: result.status as WeekSlotGeneration['status'], _imageTs: Date.now() }
            : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'done' },
      }));
    } catch {
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.generation_id === generationId ? { ...g, status: 'error' as const, error: 'Template swap failed' } : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'error' },
      }));
    }
  },

  selectTemplate: async (strategyId, channelId, generationId, templateId) => {
    const gen = get().weekGenerations.find((g) => g.generation_id === generationId);
    if (!gen) return;
    const slotId = gen.slot_id;

    set((s) => ({
      weekGenerations: s.weekGenerations.map((g) =>
        g.generation_id === generationId ? { ...g, status: 'generating' as const, error: null } : g
      ),
      weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'generating' },
    }));

    try {
      const result = await contentStudioApi.swapTemplate(strategyId, {
        channel_id: channelId ?? 0,
        generation_id: generationId,
        template_id: templateId,
      });
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.generation_id === generationId
            ? { ...g, ...result, status: result.status as WeekSlotGeneration['status'], _imageTs: Date.now() }
            : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'done' },
      }));
    } catch {
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.generation_id === generationId ? { ...g, status: 'error' as const, error: 'Template selection failed' } : g
        ),
        weekPlanProgress: { ...s.weekPlanProgress, [slotId]: 'error' },
      }));
    }
  },

  updateWeekSuggestion: (slotId, patch) =>
    set((s) => ({
      weekSuggestions: s.weekSuggestions.map((sg) =>
        sg.slot_id === slotId ? { ...sg, ...patch } : sg
      ),
    })),

  removeWeekSuggestion: (slotId) =>
    set((s) => ({
      weekSuggestions: s.weekSuggestions.map((sg) =>
        sg.slot_id === slotId ? { ...sg, skipped: true } : sg
      ),
    })),

  handleWeekPlanSSE: async (data) => {
    // Update progress immediately
    set((s) => ({
      weekPlanProgress: { ...s.weekPlanProgress, [data.slot_id]: data.status },
    }));

    if (data.status === 'done') {
      // Mark as done with generation_id first (instant UI feedback)
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.slot_id === data.slot_id
            ? { ...g, status: 'done' as const, generation_id: data.generation_id ?? g.generation_id }
            : g
        ),
      }));
      // Fetch full generation data (includes slide1_image_path, slides, etc.)
      const state = get();
      const [stratIdStr, chanIdStr] = state._weekStrategyKey.split(':');
      const strategyId = stratIdStr ? Number(stratIdStr) : null;
      const channelId = chanIdStr ? Number(chanIdStr) : undefined;
      if (strategyId) {
        try {
          const result = await contentStudioApi.getCurrentWeek(strategyId, { channel_id: channelId ?? undefined });
          const freshGen = result.generations?.find((g: { slot_id: number }) => g.slot_id === data.slot_id);
          if (freshGen) {
            set((s) => ({
              weekGenerations: s.weekGenerations.map((g) =>
                g.slot_id === data.slot_id
                  ? { ...freshGen, status: 'done' as const, error: null, _imageTs: Date.now() }
                  : g
              ),
            }));
          }
        } catch { /* user can refresh manually */ }
      }
    } else if (data.status === 'error') {
      set((s) => ({
        weekGenerations: s.weekGenerations.map((g) =>
          g.slot_id === data.slot_id
            ? { ...g, status: 'error' as const, error: data.error ?? 'Generation error' }
            : g
        ),
      }));
    }
  },

  cancelSlotSchedule: async (strategyId, publishLogId) => {
    // Find the generation_id for this publishLogId to use as progress key
    const gen = get().weekGenerations.find((g) => g.publish_log_id === publishLogId);
    const genId = gen?.generation_id;
    if (genId != null) {
      set((s) => ({ weekPlanProgress: { ...s.weekPlanProgress, [genId]: 'canceling' } }));
    }
    try {
      await contentStudioApi.cancelWeekPlanSchedule(strategyId, publishLogId);
      const channelId = useStore.getState().resolvedChannelId ?? undefined;
      await get().loadCurrentWeek(strategyId, channelId);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.httpStatus === 409) {
        set({ weekConfirmError: 'Post wird gerade veröffentlicht' });
      } else {
        set({ weekConfirmError: err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.' });
      }
    } finally {
      if (genId != null) {
        set((s) => {
          const next = { ...s.weekPlanProgress };
          delete next[genId];
          return { weekPlanProgress: next };
        });
      }
    }
  },

  resetWeekPlan: () => {
    set({ weekSuggestions: [], weekGenerations: [], weekPlanPhase: 'idle', weekPlanProgress: {} });
  },

  clearWeekPlanServer: async (strategyId, channelId) => {
    set({ weekRefreshing: true, weekConfirmError: null });
    try {
      await contentStudioApi.resetWeekPlan(strategyId, { channel_id: channelId ?? 0 });
      // Drop local state and flip phase to 'idle' so loadCurrentWeek's phase guard
      // allows re-population. The backend preserves scheduled generations, so we
      // must re-fetch to keep them visible instead of blindly clearing.
      set({
        weekSuggestions: [],
        weekGenerations: [],
        weekPlanProgress: {},
        weekPlanPhase: 'idle',
      });
      await get().loadCurrentWeek(strategyId, channelId);
    } catch (err) {
      set({ weekConfirmError: err instanceof Error ? err.message : 'Woche leeren fehlgeschlagen' });
    } finally {
      set({ weekRefreshing: false });
    }
  },

  deleteWeekGeneration: async (strategyId, generationId) => {
    set((s) => ({
      weekConfirmError: null,
      weekPlanProgress: { ...s.weekPlanProgress, [generationId]: 'deleting' },
    }));
    try {
      await contentStudioApi.deleteGeneration(strategyId, generationId);
      // Optimistic local update: loadCurrentWeek would no-op here because
      // weekPlanPhase is 'done' (the phase guard at the top of loadCurrentWeek).
      set((s) => {
        const deletedGen = s.weekGenerations.find((g) => g.generation_id === generationId);
        const remainingGenerations = s.weekGenerations.filter((g) => g.generation_id !== generationId);
        // Only drop a synthetic (persisted) suggestion that belongs to the deleted gen's slot.
        // Leave user-edited / freshly-suggested entries alone.
        const remainingSuggestions = deletedGen
          ? s.weekSuggestions.filter(
              (sg) => !(sg.slot_id === deletedGen.slot_id && sg.source === 'persisted'),
            )
          : s.weekSuggestions;
        const nextPhase = remainingGenerations.length === 0 ? 'idle' : s.weekPlanPhase;
        return {
          weekGenerations: remainingGenerations,
          weekSuggestions: remainingSuggestions,
          weekPlanPhase: nextPhase,
        };
      });
    } catch (err) {
      set({ weekConfirmError: err instanceof Error ? err.message : 'Entfernen fehlgeschlagen' });
    } finally {
      set((s) => {
        const next = { ...s.weekPlanProgress };
        delete next[generationId];
        return { weekPlanProgress: next };
      });
    }
  },

  // Engine Config
  fetchEngineConfig: async (strategyId, channelId) => {
    set({ engineConfigLoading: true });
    try {
      const result = await contentStudioApi.getEngineConfig(strategyId, channelId);
      set({ engineConfig: result, engineConfigLoading: false });
    } catch {
      set({ engineConfigLoading: false });
    }
  },

  // Cockpit Scores (with request sequencing to prevent stale overwrites)
  fetchCockpitScores: async (strategyId, channelId) => {
    const seq = (get().cockpitScoresSeq || 0) + 1;
    // Only show loading spinner on initial fetch; subsequent updates are silent
    const isInitial = get().cockpitScores === null;
    set({ cockpitScoresLoading: isInitial, cockpitScoresSeq: seq });
    try {
      const form = get().csFormState[strategyId];
      // Use defaults if form hasn't been initialized yet
      const result = await contentStudioApi.getCockpitScores(strategyId, {
        content_type: form?.contentType || 'carousel_concept',
        content_lang: form?.contentLang || 'de',
        tension_category: form?.tensionCategory ?? null,
        hook_formula: form?.hookFormula ?? null,
        experiments: form?.experiments || ['original_mode', 'rich_context', 'sharp_rules', 'guided_gen', 'account_context'],
        topic_angle: form?.topicAngle || '',
        custom_rules: form?.customRules || '',
        preset_id: form?.presetId ? String(form.presetId) : null,
        model: form?.model || 'haiku',
        competitor_count: 0,
      }, channelId);
      // Only apply if this is still the latest request
      if (get().cockpitScoresSeq === seq) {
        set({ cockpitScores: result, cockpitScoresLoading: false });
      }
    } catch {
      if (get().cockpitScoresSeq === seq) {
        set({ cockpitScoresLoading: false });
      }
    }
  },

});
